import React from 'react';
import { Button, Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';

import globalTranslations from '../../../i18n/globalTranslations';
import generatePath from '../../../util/generatePath';
import Paths from '../../../types/Paths';
import OrderTotalClass from '../../../types/OrderTotalClass';
import RouteService from '../../../services/RouteService';
import ProductTypeClass from '../../../types/ProductTypeClass';
import ProductImage from '../../product/ProductImage';
import ProductImageModel from '../../../models/ProductImage';
import { modelOf } from '../../../prop-types';
import OrderStatusLabel from '../OrderStatusLabel';
import Price from '../../product/Price';
import AccountStore from '../../../store/AccountStore';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import RecurringOrder from '../../../models/RecurringOrder';

const MAX_PRODUCT_IMAGE_COUNT = 6;

export const RecurringOrderPreview = ({
  accountStore,
  intl,
  routeService,
  onRemoveClick,
  order,
}) => {
  const getTotalPrice = () => {
    const priceTotal = order.totals.find(
      (total) => total.class === OrderTotalClass.TOTAL
    );

    return priceTotal.value || null;
  };

  const getTotalTaxlessPrice = () => {
    const priceTotal = order.totals.find(
      (total) => total.class === OrderTotalClass.TAX_FREE_TOTAL
    );

    return priceTotal.value || null;
  };

  const getOrderStatus = () => {
    if (!order.recurring_status) {
      return null;
    }
    const onHoldUntil = order.on_hold_until;

    return (
      <>
        <OrderStatusLabel status={order.recurring_status} />
        {onHoldUntil && (
          <div className="RecurringOrderPreview__order-info-hold">
            <FormattedMessage
              id="recurringOrder.onHoldSentence"
              defaultMessage="On hold until: {untilDate}"
              values={{
                untilDate: getOrderDate(),
              }}
            />
          </div>
        )}
      </>
    );
  };

  const getOrderInfo = () => {
    const allowUnderDeliveryValue = order.allow_under_delivery ? (
      <FormattedMessage {...globalTranslations.yesTitle} />
    ) : (
      <FormattedMessage {...globalTranslations.noTitle} />
    );
    const allowShippingMethodChangeValue =
      order.allow_shipping_method_change ? (
        <FormattedMessage {...globalTranslations.yesTitle} />
      ) : (
        <FormattedMessage {...globalTranslations.noTitle} />
      );

    return (
      order && (
        <>
          <div className="RecurringOrderPreview__order-info-partial-delivery">
            <FormattedMessage
              id="recurringOrder.allowUnderDeliveryValue"
              defaultMessage="Allow partial delivery when all products are not available: {allowUnder}"
              values={{
                allowUnder: <b>{allowUnderDeliveryValue}</b>,
              }}
            />
          </div>
          <div className="RecurringOrderPreview__order-info-shipping-change">
            <FormattedMessage
              id="recurringOrder.allowShippingChangeValue"
              defaultMessage="Allow automatic change of shipping method when current method is not possible: {allowUnder}"
              values={{
                allowUnder: <b>{allowShippingMethodChangeValue}</b>,
              }}
            />
          </div>
        </>
      )
    );
  };

  const getPhysicalProducts = () => {
    return order.products.filter(
      (orderProduct) => orderProduct.type === ProductTypeClass.PHYSICAL
    );
  };

  const getProductImages = () => {
    const physicalProducts = getPhysicalProducts();

    if (!physicalProducts || physicalProducts.length === 0) {
      return null;
    }

    return physicalProducts
      .filter((orderProduct, index) => index <= MAX_PRODUCT_IMAGE_COUNT)
      .map((orderProduct, index) => {
        const productImageModel = orderProduct.image_url
          ? ProductImageModel.create({
              id: index,
              product_id: orderProduct.id,
              sizes: {
                small: orderProduct.image_url,
              },
              description: '',
            })
          : null;

        const productImage = (
          <ProductImage
            product={orderProduct}
            productImage={productImageModel}
            size="small"
          />
        );

        const product = orderProduct.product_url ? (
          <MaybeExternalLink to={orderProduct.product_url}>
            {productImage}
          </MaybeExternalLink>
        ) : (
          productImage
        );

        return (
          <div
            key={orderProduct.id}
            className="RecurringOrderPreview__products-image"
          >
            {product}
          </div>
        );
      });
  };

  const getPath = (path) => {
    return routeService.getPath(generatePath(path, { id: order.id }));
  };

  const renderOrderModifyButton = () => {
    return (
      <Link to={getPath(Paths.MyAccountRecurringOrder)}>
        <Button className="RecurringOrderPreview__button-view" color="primary">
          <FormattedMessage {...globalTranslations.toOrder} />
          {' / '}
          <FormattedMessage {...globalTranslations.editTitle} />
        </Button>
      </Link>
    );
  };

  const renderOrderCancelButton = () => {
    const id = order.id;

    return (
      <Button
        className="RecurringOrderPreview__button-cancel"
        color="danger"
        onClick={() =>
          window.confirm(
            intl.formatMessage(globalTranslations.confirmRemoveTitle)
          ) && onRemoveClick(id)
        }
      >
        <FormattedMessage
          id="myAccount.cancelOrder"
          defaultMessage="Cancel order"
        />
      </Button>
    );
  };

  const getOrderDate = () => {
    const dateString = order.on_hold_until;
    return DateTime.fromISO(dateString).toLocaleString();
  };

  const title = order.title || order.autogenerated_title;

  return (
    <div className="RecurringOrderPreview">
      <Row className="RecurringOrderPreview__row">
        <Col className="RecurringOrderPreview__order-information">
          <div className="RecurringOrderPreview__link">{title}</div>
          <div className="RecurringOrderPreview__products-images">
            {getProductImages()}
          </div>
          <div className="RecurringOrderPreview__order-info">
            {getOrderInfo()}
          </div>
          <div className="RecurringOrderPreview__status">
            {getOrderStatus()}
          </div>
        </Col>
        <Col xs="auto" className="RecurringOrderPreview__order-checkout">
          <div className="RecurringOrderPreview__buttons">
            {renderOrderModifyButton()}
            {renderOrderCancelButton()}
          </div>
          <div className="RecurringOrderPreview__price">
            <span className="RecurringOrderPreview__total-sentence">
              <FormattedMessage {...globalTranslations.totalPriceSentence} />
              {':'}
            </span>
            <Price price={getTotalPrice()} />
            {accountStore.isRetailer && (
              <Price
                price={getTotalTaxlessPrice()}
                suffix={
                  <FormattedMessage {...globalTranslations.taxExcluded} />
                }
              />
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

RecurringOrderPreview.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  intl: intlShape.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  order: modelOf(RecurringOrder),
};

export default inject(
  'accountStore',
  'routeService'
)(injectIntl(observer(RecurringOrderPreview)));
